import { useNavigate } from '@tanstack/react-router'
import { type ModalFuncProps } from 'antd/es/modal/interface'
import clsx from 'clsx'
import { type ComponentPropsWithoutRef, useCallback, useState } from 'react'
import { type FieldValues } from 'react-hook-form'
import { FaCheckCircle, FaInfoCircle } from 'react-icons/fa'

import { Button, Title } from '@/components/elements'
import {
  ACTIONS,
  CONFIRMATION_MESSAGES,
  DESCRIPTIONS,
  MENU
} from '@/constants/common/message.constants'
import { ContractTypeSelect } from '@/features/contracts/components'
import { useAntdModal } from '@/libs/antd/hooks'
import { CONTRACT_TYPE, type ContractType } from '@/libs/api/types'
import { useOptionalFormContext } from '@/libs/hooks'

type Props = ComponentPropsWithoutRef<'div'> & {
  contractType?: ContractType
  tenantKey: string
}

const createContractTypeChangeConfirmModalProps = (
  onOk: ModalFuncProps['onOk']
): ModalFuncProps => ({
  title: MENU.CHANGING_CONTRACT_TYPE,
  icon: <FaInfoCircle className="mr-2 mt-[3px] text-[18px] text-yellow" />,
  content: (
    <>
      <div>{CONFIRMATION_MESSAGES.CHANGE_CONTRACT_TYPE}</div>
      <div className="mt-1">
        {DESCRIPTIONS.CONTRACT_TYPE_CHANGE_RESETS_INPUT}
      </div>
    </>
  ),
  okText: ACTIONS.CHANGE,
  cancelText: ACTIONS.CANCEL,
  onOk
})

export const ContractCreateHeader = ({
  contractType,
  tenantKey,
  className,
  ...props
}: Props) => {
  const navigate = useNavigate()
  const [modal, modalContext] = useAntdModal()
  const [type] = useState(contractType ?? null)
  const onTypeChange = (value: ContractType | null | undefined) => {
    if (type && type !== value) {
      // 既存の契約種別から変更する場合Confirmを表示する
      switch (value) {
        case CONTRACT_TYPE.UNKOU_DATE: {
          // Confirm + 運行建の新規登録ページに遷移
          modal.confirm(
            createContractTypeChangeConfirmModalProps(
              handleNavigateUnkoudateCreatePage
            )
          )
          break
        }
        case CONTRACT_TYPE.SYA_DATE: {
          // Confirm + 車建の新規登録ページに遷移
          modal.confirm(
            createContractTypeChangeConfirmModalProps(
              handleNavigateSyadateCreatePage
            )
          )
          break
        }
        case CONTRACT_TYPE.TARIFF: {
          // Confirm + タリフの新規登録ページに遷移
          modal.confirm(
            createContractTypeChangeConfirmModalProps(
              handleNavigateTariffContractPage
            )
          )
          break
        }
        default: {
          // do nothing
        }
      }
      return
    }
    if (!type) {
      switch (value) {
        case CONTRACT_TYPE.UNKOU_DATE: {
          // 運行建の新規登録ページに遷移
          handleNavigateUnkoudateCreatePage()
          break
        }
        case CONTRACT_TYPE.SYA_DATE: {
          // 車建の新規登録ページに遷移
          handleNavigateSyadateCreatePage()
          break
        }
        case CONTRACT_TYPE.TARIFF: {
          // タリフの新規登録ページに遷移
          handleNavigateTariffContractPage()
          break
        }
        default: {
          // do nothing
        }
      }
    }
  }

  const handleNavigateUnkoudateCreatePage = useCallback(() => {
    navigate({
      to: '/$tenantKey/settings/contracts/tour/create',
      params: { tenantKey },
      ignoreBlocker: true
    })
  }, [tenantKey, navigate])
  const handleNavigateSyadateCreatePage = useCallback(() => {
    navigate({
      to: '/$tenantKey/settings/contracts/charter/create',
      params: { tenantKey },
      ignoreBlocker: true
    })
  }, [tenantKey, navigate])
  const handleNavigateTariffContractPage = useCallback(() => {
    navigate({
      to: '/$tenantKey/settings/contracts/tariff/create',
      params: { tenantKey },
      ignoreBlocker: true
    })
  }, [tenantKey, navigate])

  const form = useOptionalFormContext<FieldValues>()
  const errorsCount = Object.keys(form?.formState?.errors ?? {}).length
  const disabledSave = form ? !form.formState.isValid || errorsCount > 0 : false

  return (
    <div
      {...props}
      className={clsx([
        className,
        'sticky top-0 z-30 !-mb-4 -mt-4 flex items-center justify-between bg-body pb-4 pt-4'
      ])}
    >
      <div className="flex items-center gap-4">
        <Title>{MENU.NEW_REGISTRATION}</Title>
        <div>
          <ContractTypeSelect
            value={type}
            onChange={onTypeChange}
            placeholder={DESCRIPTIONS.SELECT_CONTRACT_TYPE}
          />
        </div>
      </div>
      {!!contractType && (
        <Button
          type="primary"
          icon={<FaCheckCircle />}
          htmlType="submit"
          disabled={disabledSave}
        >
          {ACTIONS.SAVE}
        </Button>
      )}
      {modalContext}
    </div>
  )
}
